import type { IElementGenericModel3d } from "@faro-lotv/ielement-types";
import { Matrix4 } from "three";
import type { CadAnalysisReference } from "@/store/point-cloud-analysis-tool-slice";

/**
 * @param reference Saved source identity and alignment.
 * @param revision Current asset identity.
 * @param cadWorld Current CAD pose.
 * @param cloudWorld Current cloud pose.
 * @returns Whether colors can be used without silently changing the comparison reference.
 */
export function isCadReferenceCurrent(
  reference: CadAnalysisReference,
  revision: string,
  cadWorld: Matrix4,
  cloudWorld: Matrix4,
): boolean {
  return (
    revision === reference.revision &&
    cadWorld.equals(new Matrix4().fromArray(reference.cadTransform)) &&
    cloudWorld.equals(new Matrix4().fromArray(reference.cloudTransform))
  );
}

/**
 * @param model CAD descriptor.
 * @returns Source identity without persisting URLs or local paths.
 */
export function cadSourceRevision(
  model: IElementGenericModel3d,
): Promise<string> {
  return sha256Hex(
    JSON.stringify([
      model.id,
      model.uri,
      "md5Hash" in model ? model.md5Hash : undefined,
    ]),
  );
}

/**
 * @param data Text to hash.
 * @returns Lowercase hexadecimal SHA-256 digest.
 */
export async function sha256Hex(data: string): Promise<string> {
  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(data),
  );
  return Array.from(new Uint8Array(hash), (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
}
